import { useShallow } from 'zustand/react/shallow';
import clsx from 'clsx';
// mantine
import { Select, TagsInput } from '@mantine/core';
// components
import type { ITopicListItem } from '@newo/api/gateway';
import { useEffect } from 'react';
import WorkflowItem from '@/pages/Workflow/components/Filters/components/WorkflowItem';
// utils
import useWorkflowStore from '@/pages/Workflow/Workflow.store.ts';
// assets
import styles from '@/pages/Workflow/components/Filters/Filters.module.css';

type IWorkflowRecord = {
    count: number;
    name: string;
    labels: string[];
    industries: string[];
};

interface FiltersProps {
    items: ITopicListItem[];
    className?: string;
    projectAttributesScenario?: string;
}

const Filters = ({
    items,
    className,
    projectAttributesScenario
}: FiltersProps) => {
    const { filters, setFilters } = useWorkflowStore(
        useShallow((state) => ({
            filters: state.filters,
            setFilters: state.setFilters
        }))
    );

    useEffect(() => {
        if (projectAttributesScenario) {
            setFilters({
                labels: [projectAttributesScenario]
            });
        }
    }, [projectAttributesScenario, setFilters]);

    const data = items.reduce(
        (accumulator, task) => {
            for (const label of task.topic.labels) {
                if (label.endsWith('_industry'))
                    accumulator.industries.add(label);
                if (label.endsWith('_workflow')) {
                    const workflow = accumulator.workflows.get(label) ?? {
                        count: 0,
                        name: '',
                        labels: [],
                        industries: []
                    };
                    workflow.name = label;
                    workflow.count = workflow.count + 1;
                    workflow.labels = Array.from(
                        new Set([...task.topic.labels, ...workflow.labels])
                    );
                    workflow.industries = task.topic.labels.filter((l) =>
                        l.endsWith('_industry')
                    );
                    accumulator.workflows.set(label, workflow);
                }
            }
            return accumulator;
        },
        {
            industries: new Set<string>(),
            workflows: new Map<string, IWorkflowRecord>()
        }
    );

    const industriesList = Array.from(data.industries);

    useEffect(() => {
        if (!filters.workflow) {
            const first = data.workflows.keys().next().value;
            if (first) {
                setFilters({ workflow: first });
            }
        }
    }, [data.workflows, filters, setFilters]);

    const handleChange = (labels: string[]) => {
        setFilters({
            labels
        });
    };

    const handleIndustryChange = (value?: string | null) => {
        setFilters({
            industry: value ?? undefined
        });
    };
    return (
        <div className={clsx(styles.filters, className)}>
            <div className="flex items-center justify-between text-xs">
                <span className="font-semibold uppercase">Filters</span>
            </div>
            <Select
                clearable
                data={industriesList}
                label="Industry"
                placeholder="Select industry"
                value={filters.industry}
                onChange={handleIndustryChange}
                onClear={() => {
                    handleIndustryChange();
                }}
            />
            <TagsInput
                description="Press Enter to add label"
                label="Labels"
                placeholder="Start typing"
                value={filters.labels}
                onChange={handleChange}
            />
            <div>
                <div className="text-xs font-semibold uppercase">Workflows</div>
                {Array.from(data.workflows.values(), (workflow) => (
                    <WorkflowItem
                        count={workflow.count}
                        isSelected={filters.workflow === workflow.name}
                        key={workflow.name}
                        name={workflow.name}
                        onClick={() => {
                            setFilters({ workflow: workflow.name });
                        }}
                    />
                ))}
            </div>
        </div>
    );
};

export default Filters;
